import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, LinearProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

type Props = {
  itemCount: number
}

const BASE_SECONDS = 3
const PER_ITEM_SECONDS = 0.3
const MIN_ESTIMATE_SECONDS = 4
const MAX_ESTIMATE_SECONDS = 45
const PROGRESS_CAP = 97

const formatTime = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const CheckoutProcessing: React.FC<Props> = ({ itemCount }) => {
  const { t } = useTranslation()
  const startRef = useRef<number>(Date.now())
  const [elapsedMs, setElapsedMs] = useState(0)

  const estimatedTotalSeconds = useMemo(() => {
    const raw = BASE_SECONDS + Math.max(itemCount, 1) * PER_ITEM_SECONDS
    return Math.min(Math.max(raw, MIN_ESTIMATE_SECONDS), MAX_ESTIMATE_SECONDS)
  }, [itemCount])

  useEffect(() => {
    startRef.current = Date.now()
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - startRef.current)
    }, 250)
    return () => clearInterval(timer)
  }, [])

  const elapsedSeconds = Math.floor(elapsedMs / 1000)

  // Asymptotic progress: fast at first, slowing as it approaches PROGRESS_CAP.
  const progress = useMemo(() => {
    const elapsed = elapsedMs / 1000
    const ratio = 1 - Math.exp(-elapsed / estimatedTotalSeconds)
    return Math.min(ratio * PROGRESS_CAP, PROGRESS_CAP)
  }, [elapsedMs, estimatedTotalSeconds])

  return (
    <Box
      sx={{
        minHeight: '80vh',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 720, textAlign: 'center' }}>
        <Typography
          variant='h3'
          sx={{ fontWeight: 600, color: '#171737', mb: 1 }}
        >
          {t('checkout.processing.title')}
        </Typography>

        <Typography variant='body1' sx={{ color: '#6B6B6B', mb: 3 }}>
          {t('checkout.processing.subtitle')}
        </Typography>

        <Box
          component='img'
          src='/loading.gif'
          alt='Loading'
          sx={{
            width: '100%',
            maxWidth: 640,
            height: 'auto',
            display: 'block',
            mx: 'auto',
            mb: 4
          }}
        />

        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{
            height: 12,
            borderRadius: 6,
            backgroundColor: '#EFEFF3',
            '& .MuiLinearProgress-bar': {
              borderRadius: 6,
              backgroundColor: 'primary.main',
              transition: 'transform 0.3s linear'
            }
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1.5,
            color: '#8F8F8F',
            fontSize: 14
          }}
        >
          <span>{formatTime(elapsedSeconds)}</span>
          <Typography variant='h4' sx={{ fontWeight: 600, color: '#171737' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default CheckoutProcessing
